import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';

import { fieldTypes, FormData, FormProviderService, PreventDoubleSubmission } from '@fry/lib/forms';
import { RolesStore } from '@fry/lib/roles';
import { UserField, UserFieldsStore } from '@fry/lib/user-fields';
import { Observable, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Injectable()
export class UserFieldForm {

    public get pristine() {
        if (!this.formData) { return true; }
        return this.formData.pristine;
    }

    private formData: FormData | null = null;

    private fields = [
        {
            id: 'name',
            title: $localize `Name`,
            initial: '',
            widget: {
                type: 'string',
                autocomplete: 'on'
            },
            options: { required: true },
            validators: [Validators.required]
        },
        {
            id: 'fieldType',
            title: $localize `User field type`,
            initial: 'string',
            widget: {
                type: 'select',
                autocomplete: 'on'
            },
            validators: [Validators.required],
            options: {
                required: true,
                categories: Object.entries(fieldTypes).map(entry => ({
                    id: entry[0],
                    name: entry[1]
                }))
            }
        },
        {
            id: 'categories',
            title: $localize `User field categories`,
            initial: '',
            widget: {
                type: 'treeEditorFormControl',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => {
                return parent.fieldType === 'select';
            },
            validators: [],
            options: { options: { allowsHierarchy: false }}
        },
        {
            id: 'categories',
            title: $localize `User field categories`,
            initial: '',
            widget: {
                type: 'treeEditorFormControl',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'multiSelect',
            validators: [],
            options: { options: { allowsHierarchy: false }}
        },
        {
            id: 'categories',
            title: $localize `User field categories`,
            initial: '',
            widget: {
                type: 'treeEditorFormControl',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'hierarchy',
            validators: [],
            options: { options: { allowsHierarchy: true }}
        },
        // Hidden until we implement properly user list custom display
        // {
        //     id: 'isFeatured',
        //     title: 'Show this field on the users list?',
        //     initial: false,
        //     widget: {
        //         type: 'boolean',
        //     },
        //     validators: []
        // },
        {
            id: 'isRequired',
            title: $localize `Make this a required field?`,
            initial: false,
            widget: {
                type: 'boolean',
            },
            validators: []
        },
        // {
        //     id: 'isUnique',
        //     title: 'Make this a unique field?',
        //     initial: false,
        //     widget: {
        //         type: 'boolean',
        //     },
        //     validators: []
        // },
        {
            id: 'isLocked',
            title: $localize `Lock this field (non editable by users)?`,
            initial: false,
            widget: {
                type: 'boolean',
            },
            validators: []
        },
        {
            id: 'visibleBy',
            title: $localize `Who can see this user field?`,
            initial: ['__all__'],
            widget: {
                type: 'multiSelect',
                autocomplete: 'on'
            },
            validators: [Validators.required],
            options: {
                categories: []
            }
        },
        {
            id: 'fileTypes',
            title: $localize `Allowed file types`,
            initial: ['all'],
            widget: {
                type: 'multiSelect',
                autocomplete: 'on'
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'file',
            validators: [Validators.required],
            options: {
                required: true,
                categories: [
                    { id: 'all', name: 'All' },
                    { id: 'pdfs', name: 'PDFs' },
                    { id: 'words', name: 'Word documents' },
                    { id: 'csvs', name: 'CSVs' },
                    { id: 'office', name: 'Office documents' },
                    { id: 'images', name: 'Any image file' },
                    { id: 'zip', name: 'Any archive file' },
                    { id: 'videos', name: 'Any video file' },
                    { id: 'audio', name: 'Any audio file' }
                ]
            }
        },
        {
            id: 'sizeLimit',
            title: $localize `Maximum size in MB per file (up to 1000MB)`,
            initial: 100,
            widget: {
                type: 'number',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'file',
            validators: [Validators.required, Validators.max(1000)],
            options: { required: true }
        },
        {
            id: 'multiple',
            title: $localize `Allow multiple files to be uploaded`,
            initial: false,
            widget: {
                type: 'boolean',
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'file',
            validators: []
        },
        {
            id: 'fileLimit',
            title: $localize `Maximum number of files`,
            initial: 1,
            widget: {
                type: 'number',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'file' && parent.multiple,
            validators: [Validators.required],
            options: { required: true }
        },
    ];

    constructor(
        private provider: FormProviderService,
        private userFields: UserFieldsStore,
        private roles: RolesStore,
    ) {}

    init(userField: UserField): Observable<FormData> {
        const model = userField.isNew() ? userField.doc : userField.transformed;
        return this.roles.allStub().pipe(
            switchMap(roles => {
                const fields = this.fields.map(field => {
                    if (field.id === 'visibleBy') {
                        return {
                            ...field,
                            options: {
                                categories: [
                                    { id: '__all__', name: $localize `All roles`},
                                    { id: '__owner__', name: $localize `Owner`},
                                    ...roles
                                ]
                            }
                        };
                    }
                    return field;
                });
                return this.provider.getFormDataWith(fields, model);
            }),
            tap(formData => {
                this.formData = formData;
                // Add listener to reset fileLimit when multiple is toggled
                const multipleControl = formData.form.get('multiple');
                if (multipleControl) {
                    multipleControl.valueChanges.subscribe(isMultiple => {
                        const fileLimitControl = formData.form.get('fileLimit');
                        if (fileLimitControl) {
                            if (!isMultiple) {
                                // Reset to 1 when multiple is unchecked
                                fileLimitControl.setValue(1, { emitEvent: false });
                            }
                        }
                    });
                }
            })
        )
    }

    @PreventDoubleSubmission()
    submit(userField: UserField): Observable<UserField> {
        if (!this.formData || this.formData.invalid) {
            return throwError($localize `Please review the form`);
        }

        // TODO: we need to figure out how we save doc vs orig
        // const value = {...userField.doc, ...this.formData.getValue()};
        // userField.doc = value;
        // Object.assign(userField.doc, this.formData.getValue());
        userField.setValue(this.formData.getValue());
        return this.userFields.save(userField);
    }
}
